import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
// eslint-disable-next-line
// @ts-ignore
import { Radar } from 'vue-chartjs';
import { get } from 'lodash';
import { CHARACTERISTICS } from '@/constants';
import { UserProfileDTO } from '@/models';

interface Dataset {
  label?: string;
  backgroundColor?: string;
  borderColor?: string;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  pointHoverBackgroundColor?: string;
  pointHoverBorderColor?: string;
  data?: number[];
  fill?: boolean;
}

interface Legend {
  label: string;
  isActive: boolean;
  style: {
    backgroundColor: string;
    outlineColor: string;
  };
}

@Component({
  extends: Radar
})
export default class PeerRatingRadarChart extends Vue {
  @Prop() userProfileData: UserProfileDTO;
  @Prop({ default: 0 }) option: number;
  @Prop({ default: () => [] as Legend[] }) legends: Legend[];

  private _chart: any;
  private renderChart!: any;
  private readonly options = {
    responsive: true,
    elements: {
      line: {
        borderWidth: 2
      }
    },
    scale: {
      ticks: {
        beginAtZero: true,
        max: 5,
        min: 0,
        stepSize: 1
      },
      pointLabels: {
        fontSize: 14,
        fontFamily: "'Mulish', sans-serif"
      }
    },
    legend: {
      display: false
    }
  };

  private readonly yourScoreConfig: Dataset = {
    label: 'My Score',
    backgroundColor: 'rgba(73, 160, 181,0.4)',
    borderColor: 'rgb(73, 160, 181)',
    fill: true
  };

  private readonly yourKmsScoreConfig: Dataset = {
    label: 'KMS average',
    backgroundColor: 'rgba(236, 167, 68,0.4)',
    borderColor: 'rgb(236, 167, 68)',
    fill: true
  };

  get legendActive(): boolean[] {
    return this.legends.map(({ isActive }) => isActive);
  }

  get labels(): any {
    let professionalConductName = CHARACTERISTICS.PROFESSIONAL_CONDUCT.name.split(' ');
    professionalConductName = professionalConductName.map((name: string, index: number) => {
      const preName = professionalConductName[index - 1 < 0 ? 0 : index - 1];
      if (name.length < preName.length) {
        return [
          name,
          Array(preName.length - name.length + 1)
            .fill(' ')
            .join('')
        ].join('');
      }
      return name;
    });
    return [
      CHARACTERISTICS.ACCOUNTABILITY.name,
      CHARACTERISTICS.COMMUNICATION.name,
      CHARACTERISTICS.SUPPORTIVENESS.name,
      CHARACTERISTICS.PERSONAL_ATTRIBUTES.name,
      professionalConductName
    ];
  }

  get yourScore(): number[] {
    const { accountability, communication, supportiveness, personalAttributes, professionalConduct } = get(
      this.userProfileData,
      this.option === 0 ? 'criteriaRatingAverageUserThisPeriod' : 'criteriaRatingAverageUserAllTime',
      {}
    ) as any;
    return [accountability, communication, supportiveness, personalAttributes, professionalConduct];
  }

  get yourKmsScore(): number[] {
    const { accountability, communication, supportiveness, personalAttributes, professionalConduct } = get(
      this.userProfileData,
      this.option === 0 ? 'criteriaRatingAverageCompanyThisPeriod' : 'criteriaRatingAverageCompanyAllTime',
      {}
    ) as any;
    return [accountability, communication, supportiveness, personalAttributes, professionalConduct];
  }

  get chartData(): any {
    return {
      labels: this.labels,
      datasets: [
        {
          ...this.yourScoreConfig,
          data: this.yourScore
        },
        {
          ...this.yourKmsScoreConfig,
          data: this.yourKmsScore
        }
      ]
    };
  }

  @Watch('chartData', { deep: true })
  onChartDataChanged(): void {
    this.onRenderChart();
  }

  @Watch('legendActive', { deep: true })
  onLegendActiveChanged(): void {
    this.onRenderChart();
  }

  onRenderChart(): void {
    const chartData = { ...this.chartData };
    chartData.datasets = chartData.datasets.filter((_: any, index: number) => this.legendActive[index] ?? false);
    if (this._chart) {
      this._chart.destroy();
    }
    this.renderChart(chartData, this.options);
    this.$nextTick();
  }

  setMarginTop(): void {
    const canvas = this.$refs.canvas as any;
    if (canvas) {
      canvas.style.marginTop = '-10px';
    }
  }

  mounted(): void {
    this.setMarginTop();
    this.onRenderChart();
  }
}
